import { useEffect, useRef, useState } from 'react';
import { useConfig } from 'statsig-react';

import {
  LOW_STOCK,
  OUT_OF_STOCK,
  SIZING_KIT,
} from '@/app/[locale]/product/_consts';
import { styleDepletionFallback } from '@/app/[locale]/product/_consts/inventoryInsights';
import { usePDPContext } from '@/app/[locale]/product/_contexts/PDPProvider';
import { useWizardStateContext } from '@/app/[locale]/product/_contexts/WizardStateProvider';
import type { UseWizardState } from '@/app/[locale]/product/_hooks/useWizardState/types';
import Selections from '@/app/[locale]/product/_steps/enums/SizeStep';
import type { RingSpecificSizes } from '@/app/[locale]/product/_types/inventoryTypes';
import { type RingFinishEnum } from '@/app/enums/RingFinishEnum';
import { type RingStyleEnum } from '@/app/enums/RingStyleEnum';

export interface UseSizeStepReturn extends UseWizardState {
  isProductSuccess: boolean;
  ringSizeData: RingSpecificSizes[] | undefined;
  selectedSize: string;
  shippingDelayValue: number;
  sizeModal: boolean;
  subtextHref: React.RefObject<HTMLDivElement | null>;
  onSelectSize: (value: string) => void;
  setSizeModal: (value: boolean) => void;
}

const useSizeStep = (): UseSizeStepReturn => {
  const {
    currentRoute,
    setSizingSelector,
    stockStatus,
    isOuraRing4Wizard,
    ...rest
  } = useWizardStateContext();
  const { isSuccess: isProductSuccess } = usePDPContext();
  const [sizeModal, setSizeModal] = useState<boolean>(false);
  const { accessory, isAccessory, isReady, size, finish, style } = currentRoute;
  const [selectedSize, setSelectedSize] = useState<string>(size.value);
  const subtextHref = useRef<HTMLDivElement>(null);
  //TODO: GROW-1607 Clean up Gen3
  const gen3DepletionConfig = useConfig(
    'inventory_insights_gen3_depletion',
  ).config;
  const or4DepletionConfig = useConfig('inventory_insights_or4').config;

  const config = isOuraRing4Wizard ? or4DepletionConfig : gen3DepletionConfig;
  const ringSizeData =
    !isAccessory && finish.isValid && style.isValid
      ? config.get(style.value as RingStyleEnum, styleDepletionFallback)[
          finish.value as RingFinishEnum
        ].sizes
      : undefined;

  useEffect(() => {
    if (size.value !== SIZING_KIT) {
      setSizingSelector(Selections.ChooseSize);
    } else {
      setSizingSelector(Selections.SizingKit);
    }
    if (
      (stockStatus === OUT_OF_STOCK || stockStatus === LOW_STOCK) &&
      subtextHref.current
    ) {
      subtextHref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [setSizingSelector, size.value, stockStatus]);

  const handleSelectSize = (value: string) => {
    /* eagerly update state for fast ButtonV3 clicks
     * updating query params on charger-set is slower
     * than shallow route updates for rings
     */
    if (accessory.isValid && isReady) {
      currentRoute.pushAccessorySize(value);
    } else {
      currentRoute.pushSize(value);
    }
    setSelectedSize(value);
  };
  const shippingDelayValue = config.get('shippingDelayValue', 3);

  return {
    ...rest,
    currentRoute,
    isAccessory,
    isProductSuccess,
    ringSizeData,
    selectedSize,
    shippingDelayValue,
    sizeModal,
    stockStatus,
    subtextHref,
    onSelectSize: handleSelectSize,
    setSizeModal,
    setSizingSelector,
    isOuraRing4Wizard,
  };
};

export default useSizeStep;
